import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  debugIMEIAPI,
  lookupDeviceByIMEI,
  setIMEIAPIKeyRuntime,
  validateIMEI,
} from "../../../../api/imeiServices";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import {
  DEVICE_TYPES,
  IMEI_INPUT_METHODS,
} from "../../../../pages/smartDeviceInsurancePages/newSmartDevicePolicy/data";
import Input from "../../../common/Input/Input";
import FileUpload from "../../../common/fileUploadInput/fileUploadInput";

const DeviceForm = ({
  deviceType,
  imeiMethod,
  formData,
  onFormDataChange,
  errors = {},
  isProcessing = false,
  onProcessingChange,
}) => {
  const translate = useLanguageTranslate();
  const [isProcessingIMEI, setIsProcessingIMEI] = useState(false);
  const [imeiExtracted, setImeiExtracted] = useState(false);
  const [isLookingUpDevice, setIsLookingUpDevice] = useState(false);
  const [deviceLookupResult, setDeviceLookupResult] = useState(null);
  const [lookupError, setLookupError] = useState(null);
  const [imeiBlurError, setImeiBlurError] = useState(""); // Local error for onBlur validation
  
  // Update parent processing state when local processing changes
  useEffect(() => {
    if (onProcessingChange) {
      onProcessingChange(isProcessingIMEI || isLookingUpDevice);
    }
  }, [isProcessingIMEI, isLookingUpDevice, onProcessingChange]);

  // Auto-fill brand to "Apple" when MacBook is selected
  const isMacBook = deviceType === DEVICE_TYPES.MACBOOK;
  const brandValue = isMacBook ? "Apple" : formData.brand || "";

  // Auto-fill brand when MacBook is selected
  useEffect(() => {
    if (isMacBook && formData.brand !== "Apple") {
      onFormDataChange("brand", "Apple");
    }
  }, [isMacBook, formData.brand, onFormDataChange]);

  // Restore imeiExtracted state when switching back to screenshot method with existing data
  useEffect(() => {
    if (
      imeiMethod === IMEI_INPUT_METHODS.SCREENSHOT &&
      formData.imeiScreenshot &&
      formData.imeiScreenshot.uploaded &&
      formData.imeiNumber
    ) {
      // Screenshot was previously uploaded and IMEI was extracted
      setImeiExtracted(true);
    }
  }, [imeiMethod, formData.imeiScreenshot, formData.imeiNumber]);

  // IMEI lookup function
  const performDeviceLookup = async (imei) => {
    if (!validateIMEI(imei)) {
      return;
    }

    setIsLookingUpDevice(true);
    setLookupError(null);
    setDeviceLookupResult(null);

    try {
      const result = await lookupDeviceByIMEI(imei);

      if (result.success) {
        setDeviceLookupResult(result);

        // Auto-fill brand and model if available
        if (result.data.brand) {
          onFormDataChange("brand", result.data.brand);
        }
        if (result.data.model) {
          onFormDataChange("model", result.data.model);
        }

        console.log("Device lookup successful:", {
          brand: result.data.brand,
          model: result.data.model,
          fromCache: result.fromCache,
        });
      } else {
        console.error("Device lookup failed:", result.error);
        // Show toast error instead of inline error
        toast.error(
          translate(
            "Could not auto-fill device information. Please select manually.",
            "لم نتمكن من ملء معلومات الجهاز تلقائيًا. يرجى التحديد يدويًا."
          )
        );
      }
    } catch (error) {
      console.error("Device lookup error:", error);
      // Show toast error instead of inline error
      toast.error(
        translate(
          "Could not auto-fill device information. Please select manually.",
          "لم نتمكن من ملء معلومات الجهاز تلقائيًا. يرجى التحديد يدويًا."
        )
      );
    } finally {
      setIsLookingUpDevice(false);
    }
  };

  // Auto-lookup device when IMEI reaches exactly 15 digits (with debounce)
  useEffect(() => {
    const imei = formData.imeiNumber;

    console.log(
      "🔢 IMEI input changed:",
      imei,
      "Length:",
      imei ? imei.length : 0
    );

    // Clear previous lookup results if IMEI is not 15 digits
    if (!imei || imei.length !== 15) {
      setDeviceLookupResult(null);
      setLookupError(null);
      setIsLookingUpDevice(false);
      return;
    }

    // Don't lookup for MacBook devices
    if (deviceType === DEVICE_TYPES.MACBOOK) {
      console.log("🚫 Skipping lookup for MacBook device");
      return;
    }

    // Validate IMEI format
    if (!validateIMEI(imei)) {
      console.log("❌ IMEI format validation failed:", imei);
      setDeviceLookupResult(null);
      setLookupError(null);
      setIsLookingUpDevice(false);
      return;
    }

    console.log("⏳ Setting up debounced IMEI lookup for:", imei);

    // Clear previous lookup results
    setDeviceLookupResult(null);
    setLookupError(null);

    // Debounce BOTH the loading state AND the API call to avoid excessive calls
    const timeoutId = setTimeout(() => {
      console.log("🚀 Triggering IMEI lookup after debounce for:", imei);
      // Show loading state only after debounce
      setIsLookingUpDevice(true);
      setDeviceLookupResult(null);
      setLookupError(null);
      // Call API
      performDeviceLookup(imei);
    }, 2500); // 2.5 second delay after user stops typing

    return () => {
      console.log("🚫 Clearing timeout for IMEI lookup");
      clearTimeout(timeoutId);
      // Clear loading state if timeout is cancelled
      setIsLookingUpDevice(false);
    };
  }, [formData.imeiNumber, deviceType]);

  // Dynamic brand options including IMEI lookup result
  const getBrandOptions = () => {
    const baseBrands = [
      { value: "", label: translate("Select Brand", "اختر العلامة التجارية") },
      { value: "Samsung", label: "Samsung" },
      { value: "Apple", label: "Apple" },
      { value: "Huawei", label: "Huawei" },
      { value: "Xiaomi", label: "Xiaomi" },
      { value: "Oppo", label: "Oppo" },
      { value: "Other", label: translate("Other", "أخرى") },
    ];

    // Add detected brand from IMEI lookup if not already in list
    if (deviceLookupResult?.data?.brand) {
      const detectedBrand = deviceLookupResult.data.brand;
      const brandExists = baseBrands.some(
        (brand) => brand.value.toLowerCase() === detectedBrand.toLowerCase()
      );

      if (!brandExists && detectedBrand.toLowerCase() !== "other") {
        baseBrands.splice(-1, 0, {
          value: detectedBrand,
          label: detectedBrand,
        });
      }
    }

    return baseBrands;
  };

  const brandOptions = getBrandOptions();

  // Dynamic model options including IMEI lookup result
  const getModelOptions = () => {
    const baseModels = [
      { value: "", label: translate("Select Model", "اختر الطراز") },
      { value: "galaxy-s25-ultra", label: "Galaxy S25 ULTRA" },
      { value: "galaxy-s24", label: "Galaxy S24" },
      { value: "iphone-15-pro", label: "iPhone 15 Pro" },
      { value: "iphone-14", label: "iPhone 14" },
      { value: "other", label: translate("Other", "أخرى") },
    ];

    // Add detected model from IMEI lookup if not already in list
    if (deviceLookupResult?.data?.model) {
      const detectedModel = deviceLookupResult.data.model;
      const modelExists = baseModels.some(
        (model) => model.label.toLowerCase() === detectedModel.toLowerCase()
      );

      if (!modelExists && detectedModel.toLowerCase() !== "other") {
        baseModels.splice(-1, 0, {
          value: detectedModel.toLowerCase().replace(/\s+/g, "-"),
          label: detectedModel,
        });
      }
    }

    return baseModels;
  };

  const modelOptions = getModelOptions();

  // Handle file upload from FileUpload component
  const handleIMEIUpload = async (uploaded, fileName, imeiData, fileSize) => {
    if (uploaded) {
      if (imeiData && imeiData.imei) {
        // IMEI extracted successfully - auto-fill the field
        // Convert to string first (IMEI may come as number from DOKI)
        const extractedIMEI = String(imeiData.imei).replace(/\D/g, ""); // Remove non-digits
        onFormDataChange("imeiNumber", extractedIMEI);
        onFormDataChange("imeiScreenshot", { uploaded: true, fileName });
        setImeiExtracted(true);
        setIsProcessingIMEI(false);

        // Trigger device lookup for extracted IMEI
        if (validateIMEI(extractedIMEI)) {
          await performDeviceLookup(extractedIMEI);
        }
      } else {
        // Upload successful but OCR failed to extract IMEI
        onFormDataChange("imeiScreenshot", { uploaded: true, fileName });
        setImeiExtracted(false);
        setIsProcessingIMEI(false);
        
        // Show error toast when OCR fails
        toast.error(
          translate(
            "Could not auto-fill device information. Please select manually.",
            "لا يمكن ملء معلومات الجهاز تلقائياً. يرجى التحديد يدوياً."
          )
        );
      }
    } else {
      // File removed
      onFormDataChange("imeiScreenshot", null);
      onFormDataChange("imeiNumber", "");
      setImeiExtracted(false);
      setIsProcessingIMEI(false);
      setDeviceLookupResult(null);
      setLookupError(null);
    }
  };

  // Handle IMEI blur validation - show error when user focuses out with invalid IMEI
  const handleImeiBlur = () => {
    const imei = formData.imeiNumber;

    // Clear error if field is empty (will be caught by required validation on submit)
    if (!imei || imei.trim() === "") {
      setImeiBlurError("");
      return;
    }

    // Validate IMEI length
    if (imei.length !== 15) {
      setImeiBlurError(
        translate(
          "IMEI must be exactly 15 digits.",
          "يجب أن يتكوّن رقم IMEI من 15 رقمًا فقط."
        )
      );
    } else if (!/^\d+$/.test(imei)) {
      setImeiBlurError(
        translate(
          "IMEI number must contain only digits.",
          "يجب أن يحتوي رقم IMEI على أرقام فقط"
        )
      );
    } else {
      setImeiBlurError("");
    }
  };

  // Clear blur error when user starts typing again
  const handleImeiChange = (e) => {
    onFormDataChange("imeiNumber", e.target.value);
    setImeiBlurError(""); // Clear blur error when user types
  };

  const showScreenshotUpload =
    deviceType === DEVICE_TYPES.SMARTPHONE &&
    imeiMethod === IMEI_INPUT_METHODS.SCREENSHOT;
  const showIMEIField =
    deviceType === DEVICE_TYPES.SMARTPHONE ||
    deviceType === DEVICE_TYPES.TABLET ||
    deviceType === DEVICE_TYPES.SMARTWATCH;
  const showFormFields = !showScreenshotUpload || imeiExtracted;

  // Determine if fields should be disabled during IMEI lookup
  const shouldDisableFields =
    isLookingUpDevice &&
    formData.imeiNumber &&
    formData.imeiNumber.length === 15;

  // Determine if brand/model should be read-only after successful IMEI lookup
  const shouldMakeBrandReadOnly =
    isMacBook || (deviceLookupResult?.data?.brand && formData.brand);
  const shouldMakeModelReadOnly =
    deviceLookupResult?.data?.model && formData.model;

  // Debug function for setting API key at runtime
  const handleSetAPIKey = () => {
    const apiKey = "99bf6e24-3203-4f75-867e-d0664e809a12"; // From your .env file
    setIMEIAPIKeyRuntime(apiKey);
    console.log(
      "🔧 API key set in localStorage. Please refresh the page to use it."
    );
    alert("API key set! Please refresh the page and try again.");
  };

  // Debug function for testing API
  const handleDebugAPI = async () => {
    console.log("🧪 Manual API test triggered");
    console.log("🧪 Current env vars:", {
      NODE_ENV: process.env.NODE_ENV,
      REACT_APP_IMEI_API_KEY_EXISTS: !!process.env.REACT_APP_IMEI_API_KEY,
      REACT_APP_IMEI_API_KEY_LENGTH: process.env.REACT_APP_IMEI_API_KEY
        ? process.env.REACT_APP_IMEI_API_KEY.length
        : 0,
    });
    try {
      await debugIMEIAPI("123456789012345");
    } catch (error) {
      console.error("🧪 Debug API test failed:", error);
    }
  };

  return (
    <div className="device-form">
      {/* Screenshot Upload Section - Only for Smartphone + Screenshot method */}
      {showScreenshotUpload && (
        <div className="mb-6">
          <label className="block text-[#48535B] text-base not-italic font-normal leading-5 tracking-[-0.14px] mb-3">
            {translate(
              "Screenshot of IMEI number",
              "لقطة من شاشة هاتفك (screenshot) يظهر فيها رقم التعريف العالمي لأجهزة الهاتف المحمول (IMEI)"
            )}
          </label>

          <FileUpload
            name="imei_screenshot"
            toggleFileUpload={handleIMEIUpload}
            uploadedFile={formData.imeiScreenshot}
          />

          {!isProcessingIMEI && errors.imeiScreenshot && (
            <div className="mt-2 text-red-600 text-sm">
              {errors.imeiScreenshot}
            </div>
          )}

          {/* How it works instructions - Hide when IMEI is extracted */}
          {!imeiExtracted && (
            <div className="mt-4 rounded-lg">
              <p className="text-[#48535B] text-base font-normal mb-3">
                {translate(
                  "How it works:",
                  "كيف يمكنني تحميل لقطة شاشة لرقم IMEI؟"
                )}
              </p>
              <ol className="space-y-6 text-[#48535B] text-sm">
                <li className="flex gap-2 items-start">
                  <span className="font-normal bg-[#F6F8F9] text-center rounded-full min-w-[20px]">
                    1
                  </span>
                  <span className="leading-6">
                    {translate(
                      <>
                        On your device, open the phone app and dial{" "}
                        <span className="bg-[#F6F8F9] px-2 py-1 rounded" dir="ltr">
                          *#06#
                        </span>
                      </>,
                      <>
                        على جهازك الذكي، افتح تطبيق الهاتف واطلب الرقم{" "}
                        <span className="bg-[#F6F8F9] px-2 py-1 rounded" dir="ltr">
                          *#06#
                        </span>
                      </>
                    )}
                  </span>
                </li>
                <li className="flex gap-2 items-start">
                  <span className="font-normal bg-[#F6F8F9] text-center rounded-full min-w-[20px]">
                    2
                  </span>
                  <span>
                    {translate(
                      "Your device's IMEI number will automatically appear on the screen. Take a screenshot showing the IMEI.",
                      "سيظهر رقم IMEI الخاص بجهازك تلقائيًا على الشاشة، التقط صورة للشاشة (screenshot) تُظهر رقم IMEI."
                    )}
                  </span>
                </li>
                <li className="flex gap-2 items-start">
                  <span className="font-normal bg-[#F6F8F9] text-center rounded-full min-w-[20px]">
                    3
                  </span>
                  <span>
                    {translate(
                      "Upload the screenshot above, we will automatically scan for the device's IMEI number.",
                      "قم بتحميل لقطة الشاشة أعلاه، وسنقوم تلقائيًا بالبحث عن رقم IMEI للجهاز."
                    )}
                  </span>
                </li>
              </ol>
            </div>
          )}
        </div>
      )}

      {/* Form Fields - Show after IMEI extraction OR for non-screenshot methods */}
      {showFormFields && (
        <>
          {/* IMEI Number Field */}
          {showIMEIField && (
            <div className="mb-6">
              <Input
                label={translate(
                  deviceType === DEVICE_TYPES.SMARTPHONE
                    ? "IMEI Number"
                    : "IMEI Number (if applicable)",
                  "رقم التعريف العالمي لأجهزة الهاتف المحمول (IMEI)" +
                    (deviceType !== DEVICE_TYPES.SMARTPHONE ? " (إن وجد)" : "")
                )}
                type="text"
                value={formData.imeiNumber || ""}
                onChange={handleImeiChange}
                onBlur={handleImeiBlur}
                maxLength={15}
                error={!isProcessingIMEI && !isLookingUpDevice ? (imeiBlurError || errors.imeiNumber) : ""}
                disabled={shouldDisableFields}
              />

              {/* Device Lookup Status - Only show loading and errors */}
              {isLookingUpDevice && (
                <div className="mt-2 flex items-center gap-2 text-blue-600 text-sm">
                  <div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                  {translate(
                    "Looking up device information...",
                    "جارٍ البحث عن معلومات الجهاز..."
                  )}
                </div>
              )}

            </div>
          )}

          {/* Brand Dropdown */}
          <div className="mb-6">
            <Input
              label={translate("Brand", "العلامة التجارية")}
              type="select"
              value={brandValue}
              onChange={(e) => onFormDataChange("brand", e.target.value)}
              options={brandOptions}
              error={!isProcessingIMEI && !isLookingUpDevice ? errors.brand : ""}
              disabled={shouldMakeBrandReadOnly || shouldDisableFields}
              readOnly={shouldMakeBrandReadOnly}
            />
            {shouldDisableFields && (
              <div className="mt-1 text-xs text-gray-500">
                {translate(
                  "Disabled while looking up device information...",
                  "معطل أثناء البحث عن معلومات الجهاز..."
                )}
              </div>
            )}
          </div>

          {/* Model Dropdown */}
          <div className="mb-6">
            <Input
              label={translate("Model", "الموديل")}
              type="select"
              value={formData.model || ""}
              onChange={(e) => onFormDataChange("model", e.target.value)}
              options={modelOptions}
              error={!isProcessingIMEI && !isLookingUpDevice ? errors.model : ""}
              disabled={shouldMakeModelReadOnly || shouldDisableFields}
              readOnly={shouldMakeModelReadOnly}
            />
            {shouldDisableFields && (
              <div className="mt-1 text-xs text-gray-500">
                {translate(
                  "Disabled while looking up device information...",
                  "معطل أثناء البحث عن معلومات الجهاز..."
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DeviceForm;
